import { Database, withLogs } from '@core';
import { SupabaseClient } from '@supabase/supabase-js';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';

export type UserCoach = {
    userId: string;
    coachId: string;
    createdAt: string;
};

export type CoachStudent = {
    userId: string;
    name: string;
    createdAt: string;
};

export const updateUserCoaches = withLogs(
    async (supabaseClient: SupabaseClient<Database>, userId: string, desiredCoachIds: string[]): Promise<void> => {
        // Get current coach IDs
        const currentUserCoaches = await fetchUserCoaches(supabaseClient, userId);
        const currentCoachIds = currentUserCoaches.map((uc) => uc.coachId);

        // Calculate what needs to be added and removed
        const coachIdsToAdd = desiredCoachIds.filter((id) => !currentCoachIds.includes(id));
        const coachIdsToRemove = currentCoachIds.filter((id) => !desiredCoachIds.includes(id));

        // Add new coaches
        if (coachIdsToAdd.length > 0) {
            const insertPayload = coachIdsToAdd.map((coachId) => snakecaseKeys({ userId, coachId }));
            const { error: insertError } = await supabaseClient.from('user_coaches').insert(insertPayload);
            if (insertError) throw insertError;
        }

        // Remove old coaches
        if (coachIdsToRemove.length > 0) {
            const { error: deleteError } = await supabaseClient
                .from('user_coaches')
                .delete()
                .eq('user_id', userId)
                .in('coach_id', coachIdsToRemove);
            if (deleteError) throw deleteError;
        }
    },
    'updateUserCoaches',
);

export const fetchUserCoaches = withLogs(
    async (supabaseClient: SupabaseClient<Database>, userId: string): Promise<UserCoach[]> => {
        const { data, error } = await supabaseClient
            .from('user_coaches')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false });
        if (error) throw error;
        return data ? camelcaseKeys(data) : [];
    },
    'fetchUserCoaches',
);

export const fetchStudentsForCoach = withLogs(
    async (supabaseClient: SupabaseClient<Database>, coachId: string): Promise<CoachStudent[]> => {
        const { data, error } = await supabaseClient.rpc('get_students_for_coach', {
            p_coach_id: coachId,
        });
        if (error) throw error;
        if (!data) {
            return [];
        }
        const students = camelcaseKeys(data);
        return students.toSorted(
            (a, b) => a.name.localeCompare(b.name) || new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
    },
    'fetchStudentsForCoach',
);
